import { useEffect, useRef, useState, type MutableRefObject } from 'react'
import { core } from '@musinsa/pbo-core'
import { CameraOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons'
import { CommonFileMenuCode, CommonFileUploadError, getCommonFileUrls, uploadCommonFile } from '@/services/commonFile/apis'
import { CommonFileEntryRes, CommonFileItemRes } from '@/services/commonFile/types'

/** 모달이 읽는 행 필드 — `id` 는 업로드 `targetIds`, `fileId` 는 `filesByFileId` 조회 키. */
interface MediaTargetRow {
  id: number
  fileId: string | null
}

interface Props {
  open: boolean
  kind: 'video' | 'attach' | null
  row: MediaTargetRow | null
  /** 부른 화면코드 — 공통 파일 API 에 X-Menu-Code 로 실려 감사 MENU_CD 가 된다(LOGISPRD-17199). */
  menuCode: CommonFileMenuCode
  shipper: string
  filesByFileId: Map<string, CommonFileItemRes>
  /** true 면 업로드 영역을 그리지 않는다 — RGR02 반품조회(1.0 `AttachFile.js` 가 RGR02·RGR05 업로드를 막는다). */
  readOnly?: boolean
  /**
   * [저장]으로 업로드가 끝난 뒤 부른다. 대상 행이 첫 업로드(`row.fileId` 가 null)라면 서버가 새로 만든
   * `fileInfoId` 를 문자열로 함께 넘긴다 — 호출부가 그 행의 `fileId` 를 채우고 목록 조회
   * 대상에 포함시켜야 방금 올린 파일이 곧바로 보인다(리뷰 반영 — 이전엔 이 신호가 없어
   * 재조회 전까지 "등록된 파일이 없습니다"로 남았다).
   */
  onUploaded?: (newFileId?: string) => void
  onClose: () => void
}

const TITLE: Record<'video' | 'attach', string> = { video: '동영상', attach: '첨부' }
const RESOURCE_TYPE: Record<'video' | 'attach', 'VIDEO' | 'ATCH_FILE'> = { video: 'VIDEO', attach: 'ATCH_FILE' }
/** 파일 이름 링크 — 버튼 여백을 없애 글자만 보이게(데모의 파란 파일 이름 모양). */
const FILE_LINK_STYLE = { padding: 0, height: 'auto', fontSize: 13 } as const
const SECTION_TITLE_STYLE = { fontSize: 13, fontWeight: 600, marginBottom: 4 } as const
const EMPTY_STYLE = { color: 'rgba(0,0,0,0.35)', fontSize: 13, padding: '8px 0' } as const
/** 파일 이름 + 휴지통 한 묶음 — 이름 뒤에 휴지통이 붙는다(줄 끝으로 밀리지 않는다). 간격 4px = 컨덕터 데모(screenShell.css .pbo-media-manager)와 같은 값. */
const FILE_NAME_ROW_STYLE = { display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%' } as const
const FILE_NAME_STYLE = { color: '#1677ff', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 } as const
const TRASH_STYLE = { flex: 'none' } as const
const CAMERA_BOX_STYLE = {
  background: '#000',
  borderRadius: 6,
  overflow: 'hidden',
  minHeight: 240,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const
const THUMB_STYLE = { width: 48, height: 36, objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' } as const

/** 아직 서버에 올리지 않은 파일 — [저장]을 눌러야 올라간다(데모와 같은 흐름: 모달 안 편집은 초안, [취소]면 버린다). */
interface PendingFile {
  id: string
  file: File
  thumb?: string
}

/**
 * 목록에 보일 파일 이름 — 서버가 `fileName` 을 안 주는 옛 업로드가 있다(dev 실측 2026-09-30: videos[].key 만 온다 — 옛 모달에선 이름 없이 [열람]만 보였다).
 * 그때는 저장 키의 마지막 조각에서 업로드 때 붙은 UUID 접두(`<uuid>_`)를 떼어 쓴다.
 */
export const displayFileName = (entry: Pick<CommonFileEntryRes, 'key'> & { fileName?: string | null }): string => {
  if (entry.fileName) return entry.fileName
  const last = entry.key.split('/').pop() ?? entry.key
  return last.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i, '') || entry.key
}

const pad = (n: number) => String(n).padStart(2, '0')
/** 촬영·녹화 파일 이름의 시각 — 데모(DS 미디어 모달)와 같은 `yyyyMMddHHmmss`. */
const stamp = () => {
  const d = new Date()
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

/**
 * 웹캠 미리보기 — `getUserMedia` 로 스트림을 열고 닫는다. 권한 거부·장치 없음이면 안내 박스(데모와 같은 문구).
 * 녹화(동영상)는 열린 스트림을 `onStream` 으로 받아 `MediaRecorder` 를 건다.
 */
/** 카메라를 쓸 수 없을 때의 안내 박스(데모와 같은 문구·모양). 미리보기 자리와 녹화 실패 안내가 같이 쓴다. */
function CameraUnavailable() {
  return (
    <div style={CAMERA_BOX_STYLE}>
      <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, padding: 40 }}>카메라를 사용할 수 없습니다 (권한/장치 확인)</span>
    </div>
  )
}

function WebcamView({
  active,
  videoRef,
  onStream,
  onError,
}: {
  active: boolean
  videoRef: MutableRefObject<HTMLVideoElement | null>
  onStream?: (stream: MediaStream) => void
  /** 카메라를 못 열었다(권한 거부·장치 없음·비보안 컨텍스트) — 두 실패 경로가 같은 신호를 낸다. */
  onError?: () => void
}) {
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!active) return undefined
    let stream: MediaStream | null = null
    let cancelled = false
    const getUserMedia = navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices)
    if (!getUserMedia) {
      setFailed(true)
      onError?.()
      return undefined
    }
    getUserMedia({ video: true })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        stream = s
        if (videoRef.current) videoRef.current.srcObject = s
        setFailed(false)
        onStream?.(s)
      })
      .catch(() => {
        if (cancelled) return
        setFailed(true)
        onError?.()
      })
    return () => {
      cancelled = true
      stream?.getTracks().forEach((t) => t.stop())
      if (videoRef.current) videoRef.current.srcObject = null
    }
    // onStream·onError 는 부모가 매 렌더 새로 만든다 — 스트림은 active 로만 열고 닫는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, videoRef])
  if (!active) return null
  if (failed) return <CameraUnavailable />
  return (
    <div style={CAMERA_BOX_STYLE}>
      <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', maxHeight: 320, transform: 'scaleX(-1)' }} />
    </div>
  )
}

/**
 * 첨부·동영상 공용 모달 — 데모(컨덕터 DS 미디어 모달)와 같은 모양·흐름으로 실제 서버에 올린다(PD 2026-09-30 "데모로 통일, 실제 동작").
 *
 * DS `core.MediaManager` 는 쓰지 않는다 — 고른 파일을 이름만 남기고 버리며(업로드할 바이트가 없다), 녹화는 가짜 이름만 만들고,
 * 파일 이름을 누를 수 없다. 그래서 같은 배치(첨부: [촬영]·[첨부파일 올리기] / 동영상: 녹화 시간·[녹화시작]·[녹화중단] → "파일" 목록 →
 * [취소]·[저장])를 여기서 그리고, [저장] 때 공용 파일 API(prepare → presigned PUT → complete)로 올린다.
 *
 * - 파일 이름 = 열람 링크(새 탭) — #842 규약 그대로. URL 을 못 받은 파일은 눌리지 않는다.
 * - 휴지통 = **아직 저장하지 않은 파일**만. 이미 올라간 파일은 지울 API 가 없다 — TODO(api): 공용 파일 삭제 API(BE 요청).
 * - readOnly(반품조회): "파일" 목록 + [닫기]만.
 */
export function MediaModal({ open, kind, row, menuCode, shipper, filesByFileId, readOnly, onUploaded, onClose }: Readonly<Props>) {
  const resourceKey = kind === 'video' ? 'videos' : 'attachments'
  const entries: CommonFileEntryRes[] = kind && row?.fileId ? filesByFileId.get(row.fileId)?.[resourceKey] ?? [] : []

  const [urlByKey, setUrlByKey] = useState<Map<string, string>>(new Map())
  const [pending, setPending] = useState<PendingFile[]>([])
  const [isSaving, setIsSaving] = useState(false)
  // 첨부 — 촬영(웹캠) 영역 토글. 동영상 — 녹화 시간(초)·녹화 상태.
  const [camOpen, setCamOpen] = useState(false)
  const [sec, setSec] = useState(0)
  const [recording, setRecording] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  // 녹화 중 모달을 닫으면(취소) 그 녹화는 버린다 — MediaRecorder 의 onstop 은 stop() 뒤 **비동기**로 와서, 닫힌 뒤 초안에 끼어들 수 있다(moms-fe #845 봇 리뷰).
  //  버림 표시는 **녹화 한 건마다** 따로 둔다(그 녹화의 클로저 변수) — 공용 플래그 하나면 다음 녹화를 시작하며 지운 표시를 늦게 온 옛 onstop 이 못 본다(같은 리뷰 2차).
  const discardActiveRecordingRef = useRef<(() => void) | null>(null)
  // 카메라를 못 열었다(권한 거부·장치 없음) — 녹화 상태와 떼어 둔다. 녹화는 풀고 안내는 남긴다(moms-fe #845 리뷰 gyoungjun: 종전엔 거부 즉시 안내가 사라지거나, 장치가 없으면 "녹화 중"이 남아 [저장]이 잠겼다).
  const [camError, setCamError] = useState(false)
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const seqRef = useRef(0)

  // 열 때마다(다른 행·종류로 열어도) 초안·카메라를 비운다 — [취소]로 닫은 초안이 다음에 되살아나지 않게.
  useEffect(() => {
    setPending([])
    setCamOpen(false)
    setCamError(false)
    setRecording(false)
    setSec(0)
  }, [open, row?.id, kind])

  useEffect(
    () => () => {
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current)
    },
    [],
  )

  // 열람 주소는 **지금 보이는 서버 파일 키 집합**이 바뀔 때마다 다시 받는다(키를 문자열로 묶어 값으로 비교).
  //  종전엔 [open, row?.fileId, kind, shipper] 만 봐서, fileId 가 이미 있는 행에 여럿을 올리다 일부만 성공하면(모달이 열린 채)
  //  새로 붙은 파일 키의 주소를 못 받아 이름 링크가 비활성으로 남았다(moms-fe #845 봇 리뷰 2026-09-30). 행·종류가 바뀌면 키 집합도 바뀐다.
  const entryKeys = entries.map((e) => e.key).join('\n')
  useEffect(() => {
    if (!open || !entryKeys || !shipper) {
      setUrlByKey(new Map())
      return undefined
    }
    let cancelled = false
    getCommonFileUrls(menuCode, shipper, entryKeys.split('\n'))
      .then((items) => {
        if (!cancelled) setUrlByKey(new Map(items.map((item) => [item.key, item.url])))
      })
      .catch(() => {
        // URL 조회 실패 시 파일 이름 링크 disabled 유지 — 별도 toast 없이 조용히 흘린다(리뷰 반영).
      })
    return () => {
      cancelled = true // 늦게 온 옛 키 집합의 응답이 새 집합을 덮지 않게
    }
  }, [open, entryKeys, menuCode, shipper])

  const addPending = (file: File, thumb?: string) => {
    seqRef.current += 1
    const id = `p-${seqRef.current}`
    setPending((prev) => [...prev, { id, file, thumb }])
  }

  // ── 첨부: 촬영(스냅샷) — 웹캠 프레임을 JPEG 로 떠서 목록에 넣는다(데모와 같은 좌우 반전 미리보기 보정).
  const capture = () => {
    const v = videoRef.current
    if (!v || v.videoWidth === 0) {
      core.message.warning('카메라가 준비되지 않았습니다.')
      return
    }
    const canvas = document.createElement('canvas')
    canvas.width = v.videoWidth
    canvas.height = v.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.translate(canvas.width, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(v, 0, 0)
    const thumb = canvas.toDataURL('image/jpeg', 0.7)
    canvas.toBlob(
      (blob) => {
        if (blob) addPending(new File([blob], `photo_${stamp()}.jpg`, { type: 'image/jpeg' }), thumb)
      },
      'image/jpeg',
      0.9,
    )
  }

  // ── 동영상: 녹화 — 스트림이 열리면 MediaRecorder 를 건다. 초 > 0 이면 (초 + 1)초 뒤 자동 정지(데모·1.0 규칙).
  const stopRecord = () => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current)
    stopTimerRef.current = null
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    else setRecording(false)
  }
  const handleCameraError = () => {
    setRecording(false)
    setCamError(true)
  }
  const handleRecordStream = (stream: MediaStream) => {
    if (typeof MediaRecorder === 'undefined') {
      handleCameraError()
      return
    }
    const chunks: Blob[] = []
    let discarded = false
    const discard = () => {
      discarded = true
    }
    discardActiveRecordingRef.current = discard
    const recorder = new MediaRecorder(stream)
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data)
    }
    recorder.onstop = () => {
      recorderRef.current = null
      setRecording(false)
      if (discardActiveRecordingRef.current === discard) discardActiveRecordingRef.current = null
      if (discarded) return
      if (chunks.length === 0) return
      const type = recorder.mimeType || 'video/webm'
      addPending(new File(chunks, `webcam_${stamp()}.webm`, { type }))
      core.message.success('녹화 파일이 목록에 추가되었습니다.')
    }
    recorderRef.current = recorder
    recorder.start()
    if (sec > 0) stopTimerRef.current = setTimeout(stopRecord, (sec + 1) * 1000)
  }

  /** [저장] — 초안 파일을 순서대로 올린다. 일부가 실패하면 올라간 것만 목록에서 빼고 모달을 연 채 알린다(다시 [저장]으로 나머지를 올린다). */
  const save = async () => {
    if (!kind || !row) return
    if (pending.length === 0) {
      onClose()
      return
    }
    setIsSaving(true)
    let newFileId: string | undefined
    const done = new Set<string>()
    // 순서대로 — 첫 업로드가 행의 fileId 를 만든다(뒤 업로드가 같은 fileId 로 묶이게 병렬로 보내지 않는다). 실패하면 거기서 멈춘다.
    const uploadFrom = async (index: number): Promise<void> => {
      const item = pending[index]
      if (!item) return
      const completed = await uploadCommonFile(menuCode, shipper, item.file, RESOURCE_TYPE[kind], [row.id])
      if (!row.fileId && !newFileId) newFileId = String(completed.fileInfoId)
      done.add(item.id)
      await uploadFrom(index + 1)
    }
    try {
      await uploadFrom(0)
    } catch (e) {
      if (e instanceof CommonFileUploadError) {
        // eslint-disable-next-line no-console
        console.error('[MediaModal] 업로드 실패', { stage: e.stage, status: e.status, code: e.code })
        core.message.error(`업로드에 실패했습니다. (${e.message})`)
      } else {
        // eslint-disable-next-line no-console
        console.error('[MediaModal] 업로드 실패', e)
        core.message.error('업로드에 실패했습니다.')
      }
    } finally {
      setIsSaving(false)
    }
    if (done.size > 0) onUploaded?.(newFileId)
    if (done.size === pending.length) {
      core.message.success('저장되었습니다.')
      onClose()
      return
    }
    setPending((prev) => prev.filter((p) => !done.has(p.id)))
  }

  /**
   * 닫기([취소]·[닫기]·X·ESC·바깥 클릭 공통).
   * - 업로드 중에는 닫지 않는다 — 닫혀도 업로드 루프와 onUploaded 가 계속 돌아 "취소했는데 올라간" 상태가 된다(moms-fe #845 리뷰 sookeunwoo).
   *   [취소] 버튼만 막던 것을 X·ESC·바깥 클릭까지(여기 + Modal closable/keyboard/maskClosable).
   * - 녹화 중이면 멈추고 그 녹화는 버린다.
   */
  const close = () => {
    if (isSaving) return
    if (recording) {
      discardActiveRecordingRef.current?.()
      stopRecord()
    }
    onClose()
  }

  const serverFileList = entries.map((entry) => (
    // 파일 이름이 곧 열람 링크 — 데모의 파란 파일 이름과 같은 모양으로, 누르면 새 탭에서 연다(PD 확정 2026-09-30). URL 을 못 받았으면 눌리지 않는다.
    // TODO(api): 이미 올라간 파일의 삭제(휴지통) — 공용 파일 삭제 API 가 없다(BE 요청).
    <div key={entry.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <core.Button
        type="link"
        size="small"
        style={FILE_LINK_STYLE}
        disabled={!urlByKey.get(entry.key)}
        onClick={() => window.open(urlByKey.get(entry.key), '_blank', 'noopener,noreferrer')}
      >
        {displayFileName(entry)}
      </core.Button>
    </div>
  ))
  const pendingFileList = pending.map((p) => (
    // 휴지통은 파일 이름 **바로 옆**(PD 2026-09-30 "너무 떨어져 있어 인지가 어렵다") — 이름이 남는 폭을 다 차지하지 않게(flex:1 없음) 하고,
    //  긴 이름만 말줄임(minWidth 0 + 휴지통은 줄지 않게).
    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {p.thumb ? <img src={p.thumb} alt={p.file.name} style={THUMB_STYLE} /> : null}
      <span style={FILE_NAME_ROW_STYLE}>
        <span style={FILE_NAME_STYLE} title={p.file.name}>
          {p.file.name}
        </span>
        <core.Button
          size="small"
          type="text"
          aria-label={`${p.file.name} 빼기`}
          icon={<DeleteOutlined />}
          disabled={isSaving}
          style={TRASH_STYLE}
          onClick={() => setPending((prev) => prev.filter((x) => x.id !== p.id))}
        />
      </span>
    </div>
  ))
  const fileSection = (
    <div>
      <div style={SECTION_TITLE_STYLE}>파일</div>
      {entries.length + pending.length === 0 ? (
        <div style={EMPTY_STYLE}>등록된 파일이 없습니다.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 160, overflowY: 'auto', padding: '4px 0' }}>
          {serverFileList}
          {pendingFileList}
        </div>
      )}
    </div>
  )

  const editor =
    kind === 'video' ? (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>녹화 시간 (초)</span>
          <core.InputNumber
            size="small"
            min={0}
            value={sec}
            onChange={(v) => setSec(Number(v ?? 0))}
            style={{ width: 80 }}
            disabled={recording || isSaving}
          />
          {recording ? (
            <core.Button size="small" danger onClick={stopRecord}>
              녹화중단
            </core.Button>
          ) : (
            <core.Button
              size="small"
              type="primary"
              disabled={isSaving}
              onClick={() => {
                setCamError(false)
                setRecording(true)
              }}
            >
              녹화시작
            </core.Button>
          )}
          {recording ? <span style={{ color: '#f5222d', fontSize: 12 }}>● 녹화 중{sec > 0 ? ` (${sec + 1}초 후 자동 정지)` : ''}</span> : null}
        </div>
        {/* 녹화할 때마다 미리보기를 **새로 마운트**한다 — 계속 마운트해 두면 지난 실패 표시(failed)가 남아, 재시도가 성공해도 <video> 가
            스트림이 온 뒤에야 붙어 srcObject 가 빠진 검은 화면으로 녹화됐다(moms-fe #845 리뷰 gyoungjun). 첨부의 [촬영]과 같은 방식. */}
        {recording ? <WebcamView active videoRef={videoRef} onStream={handleRecordStream} onError={handleCameraError} /> : null}
        {camError && !recording ? <CameraUnavailable /> : null}
      </>
    ) : (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <core.Button
            size="small"
            icon={<CameraOutlined />}
            disabled={isSaving}
            onClick={() => {
              setCamError(false)
              setCamOpen((o) => !o)
            }}
          >
            {camOpen ? '카메라 닫기' : '촬영'}
          </core.Button>
          <core.Upload
            multiple
            showUploadList={false}
            disabled={isSaving}
            beforeUpload={(file: File) => {
              addPending(file)
              return false
            }}
          >
            <core.Button size="small" icon={<UploadOutlined />} disabled={isSaving}>
              첨부파일 올리기
            </core.Button>
          </core.Upload>
        </div>
        {camOpen ? (
          <>
            {/* 카메라를 못 열면 카메라 영역을 닫고(캡쳐 버튼이 남지 않게) 안내만 남긴다 — 동영상과 같은 처리(#845 봇 리뷰). [촬영]을 다시 누르면 재시도. */}
            <WebcamView
              active={camOpen}
              videoRef={videoRef}
              onError={() => {
                setCamOpen(false)
                setCamError(true)
              }}
            />
            <core.Button size="small" onClick={capture} style={{ alignSelf: 'center', width: 120 }}>
              캡쳐
            </core.Button>
          </>
        ) : null}
        {camError && !camOpen ? <CameraUnavailable /> : null}
      </>
    )

  return (
    <core.Modal
      open={open}
      title={kind ? TITLE[kind] : ''}
      width={560}
      onCancel={close}
      closable={!isSaving}
      keyboard={!isSaving}
      maskClosable={!isSaving}
      footer={
        readOnly ? (
          <core.Button onClick={close}>닫기</core.Button>
        ) : (
          <>
            <core.Button onClick={close} disabled={isSaving}>
              취소
            </core.Button>
            <core.Button
              type="primary"
              loading={isSaving}
              disabled={recording}
              onClick={() => {
                save().catch(() => undefined) // 실패 안내는 save 안에서 한다
              }}
            >
              저장
            </core.Button>
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {kind && !readOnly ? editor : null}
        {fileSection}
      </div>
    </core.Modal>
  )
}
